import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconBell } from '../../components/icons';
import { useShippingAlerts } from './useShippingAlerts';

function AlertItem({ alert, onClick }) {
  return (
    <li>
      <Link
        to={alert.type === 'priority' ? `/admin/customer-service?search=${alert.amazonOrderId}` : `/admin/orders/${alert.orderId}`}
        onClick={onClick}
        className="block rounded-lg border border-red-100 bg-red-50 hover:bg-red-100/75 px-3 py-2.5 text-sm leading-relaxed text-red-950 transition"
      >
        {alert.type === 'priority' ? (
          <>
            <span className="inline-flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
              ★ Priority CS
            </span>
            <strong className="ml-1.5 font-semibold text-red-950 block mt-1">{alert.label}</strong> requires urgent Customer Service follow-up.
            <span className="mt-1 block text-xs text-red-700">Order {alert.amazonOrderId}</span>
          </>
        ) : alert.type === 'overdue' ? (
          <>
            <strong className="font-semibold">{alert.label}</strong> is overdue — ship immediately.
            <span className="mt-1 block text-xs text-red-700">Order {alert.amazonOrderId}</span>
          </>
        ) : (
          <>
            <strong className="font-semibold">{alert.label}</strong> has{' '}
            {alert.hoursLeft === 1 ? '1hr' : `${alert.hoursLeft}hr`} left to ship.
            <span className="mt-1 block text-xs text-red-700">Order {alert.amazonOrderId}</span>
          </>
        )}
      </Link>
    </li>
  );
}

export function ShippingNotificationBell({ token }) {
  const { alerts, loading } = useShippingAlerts(token);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const urgentCount = alerts.length;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!token || urgentCount === 0) {
    return null;
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className="relative inline-grid h-10 w-10 place-items-center rounded-full border border-red-200 bg-red-50 text-red-700 transition hover:bg-red-100"
        aria-label={`${urgentCount} urgent notification${urgentCount === 1 ? '' : 's'}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((current) => !current)}
      >
        <IconBell className="h-5 w-5" />
        <span className="absolute -right-1 -top-1 inline-flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
          {urgentCount > 9 ? '9+' : urgentCount}
        </span>
      </button>

      {open ? (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">Dashboard alerts</p>
            <p className="mt-0.5 text-xs text-slate-500">
              Urgent shipping deadlines and customer service follow-ups
            </p>
          </div>

          <div className="max-h-80 overflow-y-auto px-3 py-3">
            {loading ? (
              <p className="px-1 py-2 text-sm text-slate-500">Checking for alerts…</p>
            ) : (
              <ul className="grid gap-2">
                {alerts.map((alert) => (
                  <AlertItem
                    key={`${alert.orderId}-${alert.amazonOrderId}-${alert.type}`}
                    alert={alert}
                    onClick={() => setOpen(false)}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-slate-100 px-4 py-3">
            <Link
              to="/admin/unshipped-orders"
              className="text-sm font-semibold text-blue-700 hover:text-blue-800"
              onClick={() => setOpen(false)}
            >
              View all unshipped orders
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
